import { useReducedMotion } from 'framer-motion';

/**
 * Large completion ring for CabinProgress hero.
 * Shows a thick circular progress with label inside.
 *
 * Props:
 *   progress       — 0-100
 *   size           — px (default 120)
 *   strokeWidth    — (default 10)
 *   color          — gradient start (default brand-primary)
 *   label          — text inside ring (e.g. "9%")
 *   sublabel       — smaller text below label
 *   gradient       — tailwind gradient classes for svg stroke (uses inline style instead)
 *   gradientColors — [startColor, endColor] for SVG gradient (default brand colors)
 */
export default function CompletionRing({
  progress = 0,
  size = 120,
  strokeWidth = 10,
  label,
  sublabel,
  gradientColors = ['#0EA5E9', '#6366F1'],
  trackColor = '#E5E7EB',
}) {
  const shouldReduce = useReducedMotion();
  const radius = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(100, Math.max(0, progress)) / 100) * circumference;
  const center = size / 2;
  const gradId = `completion-ring-grad-${size}`;

  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
      role="img"
      aria-label={`${progress}% tamamlandı`}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: 'rotate(-90deg)', display: 'block' }}
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={gradientColors[0]} />
            <stop offset="100%" stopColor={gradientColors[1]} />
          </linearGradient>
        </defs>
        {/* Track */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
        />
        {/* Progress arc */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={`url(#${gradId})`}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition: shouldReduce ? 'none' : 'stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1)',
          }}
        />
      </svg>

      {/* Center label */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center text-center"
        aria-hidden="true"
      >
        {label && (
          <span className="text-2xl font-black text-[var(--text-main)] leading-none">{label}</span>
        )}
        {sublabel && (
          <span
            className="text-[10px] text-[var(--text-muted)] mt-1 leading-tight text-center"
            style={{ maxWidth: size - strokeWidth * 4 }}
          >
            {sublabel}
          </span>
        )}
      </div>
    </div>
  );
}
